'use strict';

import { UserApiTokenCollection } from '../../Collections/User/UserApiTokenCollection';
import { UserApiToken } from '../../Entities/User/UserApiToken';
import type { DeleteResponse } from '../CoreRepository';
import { CoreRepository } from '../CoreRepository';

/**
 * Payload for creating/updating a UserApiToken.
 */
export interface UserApiTokenPayload {
  name?: string;
  expiresAt?: string | null;
}


/**
 * Repository for UserApiToken entities.
 */
export class UserApiTokenRepository extends CoreRepository {
  /**
   * Retrieve a paginated list of API tokens for a given user.
   *
   * @param userId - User ID to fetch API tokens for.
   * @param options - Query options such as limit, offset.
   * @param criteria - Additional filter criteria passed to the API.
   * @returns A hydrated collection with pagination info.
   */
  public getUserApiTokens(
    userId: number,
    options: object = {},
    criteria: object = null
  ): Promise<UserApiTokenCollection> {
    if (!options) options = {};
    if (criteria) (options)['criteria'] = criteria;

    return this.webAPI
      .getUserApiTokens(userId, options)
      .then((response) => {
        const collection = new UserApiTokenCollection();

        response.data.forEach((collectionItemData) => {
          const collectionItem = new UserApiToken();
          collectionItem.hydrate(collectionItemData);
          collection.addItem(collectionItem);
        });

        collection.setOffset(response.offset ?? 0);
        collection.setLimit(response.limit ?? 0);
        collection.setTotal(response.total ?? 0);

        return collection;
      });
  }

  /**
   * Retrieve a single API token by ID.
   *
   * @param userId - User ID.
   * @param tokenId - API Token ID to fetch.
   * @param options - Optional query options.
   * @returns Hydrated UserApiToken instance.
   */
  public getUserApiToken(userId: number, tokenId: number, options: object = {}): Promise<UserApiToken> {
    return this.webAPI.getUserApiToken(userId, tokenId, options).then((response) => {
      const item = new UserApiToken();
      item.hydrate(response);
      return item;
    });
  }

  /**
   * Create a new API token for a user.
   *
   * @param userId - User ID.
   * @param data - Data to send to the API.
   * @returns Hydrated UserApiToken instance after creation.
   */
  public createUserApiToken(userId: number, data: UserApiTokenPayload): Promise<UserApiToken> {
    return this.webAPI.createUserApiToken(userId, data).then((response) => {
      const item = new UserApiToken();
      item.hydrate(response);
      return item;
    });
  }

  /**
   * Update an existing API token.
   *
   * @param userId - User ID.
   * @param tokenId - API Token ID to update.
   * @param data - Data to send to the API.
   * @returns Hydrated UserApiToken instance after update.
   */
  public updateUserApiToken(userId: number, tokenId: number, data: UserApiTokenPayload): Promise<UserApiToken> {
    return this.webAPI.updateUserApiToken(userId, tokenId, data).then((response) => {
      const item = new UserApiToken();
      item.hydrate(response);
      return item;
    });
  }

  /**
   * Delete an API token.
   *
   * @param userId - User ID.
   * @param tokenId - API Token ID to delete.
   * @returns Result of the delete operation.
   */
  public deleteUserApiToken(userId: number, tokenId: number): Promise<DeleteResponse> {
    return this.webAPI.deleteUserApiToken(userId, tokenId);
  }
}

